import { AbstractTemplateElementComponent } from './AbstractTemplateElementComponent';
import { tag } from '../../utils/tag';
import { BaitStates } from '../../constants/BaitStates';

export class PlaygroundComponent extends AbstractTemplateElementComponent {
    #rootEl;
    #sizeEl;
    #wrapEl;
    #rowsList = [[]];
    #w = 0;
    #h = 0;
    #rotate = false;

    constructor(w = 10, h = 10) {
        super();

        this.#w = w;
        this.#h = h;
    }

    _render(parentNode) {
        const rootEl = tag('div', 'playground');
        const sizeEl = tag('div', 'playground__size');
        const wrapEl = tag('div', 'playground__wrap');

        rootEl.setAttribute('role', 'img');

        sizeEl.style.paddingTop = `${(100 * this.#h) / this.#w}%`;

        this.#renderInnerElements(wrapEl);

        rootEl.appendChild(sizeEl);
        rootEl.appendChild(wrapEl);
        parentNode.appendChild(rootEl);

        this.#rootEl = rootEl;
        this.#sizeEl = sizeEl;
        this.#wrapEl = wrapEl;

        this.#updateSizeStyles(this.#w, this.#h);

        return [rootEl];
    }

    #renderInnerElements(rootEl) {
        const d = 10;
        const gridEl = tag('svg', 'playground-grid');
        const cellsEl = tag('span', 'playground-cells');
        const rowsList = [];

        gridEl.setAttributeNS(null, 'viewBox', `0 0 ${this.#w * d} ${this.#h * d}`);
        gridEl.setAttribute('width', this.#w * d);
        gridEl.setAttribute('height', this.#h * d);
        gridEl.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        gridEl.setAttribute('version', '1.1');

        const setStroke = (l) => {
            l.setAttribute('stroke', 'white');
            l.setAttribute('stroke-width', 1);
            l.setAttribute('vector-effect', 'non-scaling-stroke');
        };

        const appendRowCells = (i, num) => {
            const rowList = [];

            for (let j = 0; j < num; j += 1) {
                const c = tag('span', 'cell');
                const ci = tag('span', 'cell__inner');

                c.setAttribute('aria-hidden', true);
                c.setAttribute('data-cell-indices', `${i} ${j}`);
                c.classList.add('playground-cells__cell');
                c.classList.add(`playground-cells__cell--${i}-${j}`);

                c.appendChild(ci);
                cellsEl.appendChild(c);
                rowList.push(c);
            }

            return rowList;
        };

        for (let i = 0; i < this.#w - 1; i += 1) {
            const l = tag('line', 'playground-grid__line');

            l.setAttribute('x1', (i + 1) * d);
            l.setAttribute('x2', (i + 1) * d);
            l.setAttribute('y1', 0);
            l.setAttribute('y2', this.#h * d);
            setStroke(l);

            gridEl.appendChild(l);
        }

        for (let i = 0; i < this.#h - 1; i += 1) {
            const l = tag('line', 'playground-grid__line');

            l.setAttribute('y1', (i + 1) * d);
            l.setAttribute('y2', (i + 1) * d);
            l.setAttribute('x1', 0);
            l.setAttribute('x2', this.#w * d);
            setStroke(l);

            rowsList.push(appendRowCells(i, this.#w));
            gridEl.appendChild(l);
        }
        rowsList.push(appendRowCells(this.#h - 1, this.#w));

        const divEl = tag('div', 'playground__grid', gridEl.outerHTML);

        cellsEl.classList.add('playground__cells');

        rootEl.appendChild(divEl);
        rootEl.appendChild(cellsEl);

        this.#rowsList = rowsList;
    }

    _clear() {
        super._clear();
        this.#rootEl = null;
        this.#sizeEl = null;
        this.#wrapEl = null;
        this.#rowsList = null;
    }

    setSize(w, h) {
        if (!this.#rootEl) {
            this.#w = w;
            this.#h = h;

            return;
        }

        if (w === this.#w && h === this.#h) {
            this.#rootEl.classList.remove('playground--rotate');
            this.#rotate = false;

            this.#updateSizeStyles(this.#w, this.#h);

            return;
        }

        if (w === this.#h && h === this.#w) {
            this.#rootEl.classList.add('playground--rotate');
            this.#rotate = true;

            this.#updateSizeStyles(this.#h, this.#w);

            return;
        }

        this.#w = w;
        this.#h = h;
        this.#rootEl.classList.remove('playground--rotate');
        this.#rootEl.innerHTML = '';

        this.#renderInnerElements(this.#rootEl);
    }

    #updateSizeStyles(w, h) {
        if (!this.#rootEl) return;

        const dw = 100 / w;

        this.#sizeEl.style.paddingTop = `${dw * h}%`;
        this.#wrapEl.setAttribute('style', `--cell-width: ${dw}%; --grid-scale: ${this.#w / w};`);
    }

    updateCells(cellsState, flipped) {
        if (!this.#rowsList) return;

        cellsState.forEach((item) => {
            let cell = this.#rowsList[flipped ? item.col : item.row];

            if (!cell) return;

            cell = cell[flipped ? item.row : item.col];
            // console.log(cell, item.row, item.col, item);

            if (!cell) return;

            switch (item.state) {
                case BaitStates.IDLE: {
                    cell.classList.add(`cell--${item.viewType}`);
                    cell.classList.add('active');
                    cell.classList.remove('inactive');

                    break;
                }
                case BaitStates.EATEN: {
                    cell.classList.add(`cell--${item.viewType}`);
                    cell.classList.remove('active');
                    cell.classList.add('inactive');

                    break;
                }
                case BaitStates.EMPTY:
                default: {
                    cell.classList.remove(`cell--${item.viewType}`);
                    cell.classList.remove('active');
                    cell.classList.remove('inactive');

                    break;
                }
            }
        });
    }

    clearCells() {
        if (!this.#rowsList) return;

        this.#rowsList.forEach((row, j) => {
            row.forEach((cell, i) => {
                cell.setAttribute('class', '');
                cell.classList.add('playground-cells__cell');
                cell.classList.add(`playground-cells__cell--${i}-${j}`);
            });
        });
    }

    get size() {
        if (this.#rotate) {
            return {
                w: this.#h,
                h: this.#w
            };
        }

        return {
            w: this.#w,
            h: this.#h
        };
    }
}
